import { useEffect } from "react";
import { ALL_FEATURES, AirshipFeature } from "../types";
import { clearAirshipNamedUser, getAirshipNamedUser, setAirshipNamedUser, } from "../utils/localStorage";
/**
 * Hook that manages Airship privacy settings and contact identification.
 * Enables/disables SDK features based on tracking consent and manages user identity.
 *
 * @param hasTrackingConsent - Whether user has consented to tracking features
 * @param userDetails - Object containing userId, brand, and businessType for identification
 * @param sdk - Optional Airship SDK instance
 * @param isOptedIn - Optional flag indicating if the user is opted in to tracking
 */
export function useAirshipPrivacy(hasTrackingConsent, userDetails, sdk, isOptedIn) {
    useEffect(() => {
        if (!sdk) {
            return;
        }
        if (!isOptedIn) {
            return;
        }
        const managePrivacy = async () => {
            const { privacyManager, contact } = sdk;
            const featuresToKeep = [AirshipFeature.Push, AirshipFeature.Contacts];
            const currentlyEnabled = await privacyManager.listEnabled();
            if (hasTrackingConsent) {
                await privacyManager.enable(...ALL_FEATURES);
            }
            else {
                const featuresToDisable = currentlyEnabled.filter((f) => !featuresToKeep.includes(f));
                if (featuresToDisable.length > 0) {
                    await privacyManager.disable(...featuresToDisable);
                }
            }
            const { userId, brand, businessType } = userDetails;
            const storedNamedUser = getAirshipNamedUser();
            if (hasTrackingConsent && userId) {
                const formattedId = formatUserId(userId, brand, businessType);
                if (storedNamedUser !== formattedId) {
                    await contact.identify(formattedId);
                    setAirshipNamedUser(formattedId);
                }
            }
            else if (!hasTrackingConsent && storedNamedUser) {
                await contact.reset();
                clearAirshipNamedUser();
            }
        };
        managePrivacy().catch((error) => {
            console.error("[Airship] Failed to manage privacy settings:", error);
        });
    }, [sdk, hasTrackingConsent, userDetails, isOptedIn]);
}
function formatUserId(userId, brand, businessType) {
    return `${businessType}_${brand}_${userId}`;
}
